import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const SessionList = () => {
  const [sessions, setSessions] = useState([]);
  const [focusArea, setFocusArea] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [newSession, setNewSession] = useState({
    date: '',
    focusArea: '',
    coachId: '',
    playerIds: [],
    isRecurring: false,
    recurrenceWeeks: 1,
  });
  const [coaches, setCoaches] = useState([]);
  const [players, setPlayers] = useState([]);

  const navigate = useNavigate();

  const fetchSessions = () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    const params = {};
    if (focusArea) params.focusArea = focusArea;
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }

    axios
      .get('https://cricket-academy-backend.onrender.com/api/sessions', {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => setSessions(res.data))
      .catch((err) => {
        console.error('Session fetch error:', err.response?.status, err.response?.data || err.message);
        alert('Failed to load sessions');
      });
  };

  const fetchCoachesAndPlayers = () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    axios
      .get('https://cricket-academy-backend.onrender.com/api/admin/coaches', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setCoaches(res.data))
      .catch((err) => console.error('Coach fetch error:', err));

    axios
      .get('https://cricket-academy-backend.onrender.com/api/admin/players', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setPlayers(res.data))
      .catch((err) => console.error('Player fetch error:', err));
  };

  useEffect(() => {
    fetchSessions();
    fetchCoachesAndPlayers();
  }, []);
  const handleCreateSession = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    const basePayload = {
      focusArea: newSession.focusArea.charAt(0).toUpperCase() + newSession.focusArea.slice(1),
      coach: newSession.coachId,
      players: newSession.playerIds,
      isRecurring: newSession.isRecurring,
      status: 'Active',
      notes: '',
      recurrencePattern: newSession.isRecurring
        ? {
            dayOfWeek: new Date(newSession.date).toLocaleDateString('en-US', { weekday: 'long' }),
            recurrenceGroupId: `${newSession.coachId}-${Date.now()}`,
          }
        : undefined,
    };

    const sessionDates = [new Date(newSession.date)];
    if (newSession.isRecurring) {
      for (let i = 1; i < newSession.recurrenceWeeks; i++) {
        const nextDate = new Date(newSession.date);
        nextDate.setDate(nextDate.getDate() + i * 7);
        sessionDates.push(nextDate);
      }
    }

    const request = editingSessionId
      ? axios.put(
          `https://cricket-academy-backend.onrender.com/api/sessions/${editingSessionId}`,
          { ...basePayload, date: sessionDates[0].toISOString() },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      : Promise.all(
          sessionDates.map((date) =>
            axios.post(
              'https://cricket-academy-backend.onrender.com/api/sessions',
              { ...basePayload, date: date.toISOString() },
              { headers: { Authorization: `Bearer ${token}` } }
            )
          )
        );

    request
      .then(() => {
        alert(editingSessionId ? 'Session updated' : 'Session(s) created');
        setNewSession({
          date: '',
          focusArea: '',
          coachId: '',
          playerIds: [],
          isRecurring: false,
          recurrenceWeeks: 1,
        });
        setEditingSessionId(null);
        setShowForm(false);
        fetchSessions();
      })
      .catch((err) => {
        console.error('Session save error:', err.response?.data || err.message);
        alert('Failed to save session');
      });
  };

  const handleEditSession = (session) => {
    setShowForm(true);
    setNewSession({
      date: session.date.slice(0, 10),
      focusArea: session.focusArea.toLowerCase(),
      coachId: session.coach?._id || '',
      playerIds: session.players?.map(p => p._id) || [],
      isRecurring: false,
      recurrenceWeeks: 1,
    });
    setEditingSessionId(session._id);
  };

  const handleDeleteSession = (id) => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');

    if (!window.confirm('Are you sure you want to delete this session?')) return;

    axios
      .delete(`https://cricket-academy-backend.onrender.com/api/sessions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert('Session deleted');
        fetchSessions();
      })
      .catch((err) => {
        console.error('Delete error:', err.response?.data || err.message);
        alert('Failed to delete session');
      });
  };
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-6 space-y-6">
        <h1 className="text-3xl font-bold text-purple-700">Session Management</h1>

        <button
          onClick={() => navigate('/admin/dashboard')}
          className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
        >
          ← Back to Dashboard
        </button>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700">Focus Area</label>
            <select
              value={focusArea}
              onChange={(e) => setFocusArea(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
            >
              <option value="">All</option>
              <option value="batting">Batting</option>
              <option value="bowling">Bowling</option>
              <option value="fielding">Fielding</option>
              <option value="combined">Combined</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          <button
            onClick={fetchSessions}
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            Filter
          </button>
        </div>

        {/* Toggle Form Button */}
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingSessionId(null);
            setNewSession({
              date: '',
              focusArea: '',
              coachId: '',
              playerIds: [],
              isRecurring: false,
              recurrenceWeeks: 1,
            });
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : 'Create New Session'}
        </button>

        {/* Create/Edit Session Form */}
        {showForm && (
          <div className="mt-8 border-t pt-6">
            <h2 className="text-xl font-semibold text-purple-700 mb-4">
              {editingSessionId ? 'Edit Session' : 'Create New Session'}
            </h2>
            <form onSubmit={handleCreateSession} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <input
                  type="date"
                  value={newSession.date}
                  onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Focus Area</label>
                <select
                  value={newSession.focusArea}
                  onChange={(e) => setNewSession({ ...newSession, focusArea: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  required
                >
                  <option value="">Select</option>
                  <option value="batting">Batting</option>
                  <option value="bowling">Bowling</option>
                  <option value="fielding">Fielding</option>
                  <option value="combined">Combined</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Coach</label>
                <select
                  value={newSession.coachId}
                  onChange={(e) => setNewSession({ ...newSession, coachId: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  required
                >
                  <option value="">Select Coach</option>
                  {coaches.map(coach => (
                    <option key={coach._id} value={coach._id}>
                      {coach.firstName} {coach.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Players</label>
                <select
                  multiple
                  value={newSession.playerIds}
                  onChange={(e) =>
                    setNewSession({
                      ...newSession,
                      playerIds: Array.from(e.target.selectedOptions, opt => opt.value),
                    })
                  }
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                  required
                >
                  {players.map(player => (
                    <option key={player._id} value={player._id}>
                      {player.firstName} {player.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={newSession.isRecurring}
                    onChange={(e) =>
                      setNewSession({ ...newSession, isRecurring: e.target.checked })
                    }
                    className="mr-2"
                  />
                  Repeat weekly
                </label>

                {newSession.isRecurring && (
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={newSession.recurrenceWeeks}
                    onChange={(e) =>
                      setNewSession({
                        ...newSession,
                        recurrenceWeeks: parseInt(e.target.value),
                      })
                    }
                    className="border-gray-300 rounded-md shadow-sm px-2 py-1 w-24"
                    placeholder="Weeks"
                  />
                )}
              </div>

              <button
                type="submit"
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                {editingSessionId ? 'Update Session' : 'Create Session'}
              </button>
            </form>
          </div>
        )}

        {/* Session List */}
        {sessions.length > 0 ? (
          <div className="space-y-4">
            {sessions.map((session) => (
              <div key={session._id} className="border p-4 rounded shadow-sm bg-purple-50">
                <p><strong>Date:</strong> {new Date(session.date).toLocaleDateString()}</p>
                <p><strong>Focus Area:</strong> {session.focusArea}</p>
                <p><strong>Coach:</strong> {session.coach?.firstName} {session.coach?.lastName}</p>
                <p><strong>Players:</strong> {session.players?.map(p => `${p.firstName} ${p.lastName}`).join(', ')}</p>

                <div className="mt-2 flex gap-4">
                  <button
                    onClick={() => handleEditSession(session)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteSession(session._id)}
                    className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No sessions found.</p>
        )}
      </div>
    </div>
  );
};

export default SessionList;